/**
 * Badge — the single, canonical badge pill.
 *
 * Renders ONE badge from a canonical `Badge` object (see config.ts / the badge
 * data-model spec). This is the only place badge pill markup lives; every
 * surface (cards, hero, carousels, brand page) renders badges through this
 * component (usually via <BadgeList />) so they look identical sitewide.
 *
 * Styling is token-driven: colors/label/emoji come from the Badge object, which
 * is itself derived from BADGE_CONFIGS. If a server payload omits presentation
 * tokens (only sends `type`), we fall back to BADGE_CONFIGS[type] so the pill
 * still renders correctly.
 */

import { BADGE_CONFIGS, type Badge as BadgeData } from './config'

export type BadgeSize = 'sm' | 'md' | 'lg'

interface BadgeProps {
  badge: BadgeData
  /** sm = mini/thumbnail, md = card grids (default), lg = product hero */
  size?: BadgeSize
  /** show the emoji prefix (default true) */
  showEmoji?: boolean
  className?: string
}

// Size tokens. Keep in sync with the legacy card/hero treatments so the visual
// switch-over is a no-op:
//  - md mirrors the old ProductCardBadges pill (text-xs px-2 py-1 rounded)
//  - lg mirrors the old ProductHeroBadges pill (px-2.5 py-1.5 rounded-lg semibold)
const SIZE_CLASSES: Record<BadgeSize, string> = {
  sm: 'text-[10px] px-1.5 py-0.5 rounded gap-0.5 font-medium',
  md: 'text-xs px-2 py-1 rounded gap-1 font-medium shadow-sm',
  lg: 'text-xs px-2.5 py-1.5 rounded-lg gap-1 font-semibold shadow-lg',
}

export function Badge({
  badge,
  size = 'md',
  showEmoji = true,
  className = '',
}: BadgeProps) {
  // Token resolution: prefer the values on the Badge object, fall back to the
  // central config keyed by type (robust against minimal server payloads).
  const config = BADGE_CONFIGS[badge.type]
  const bgColor = badge.bgColor || config?.bgColor || 'bg-gray-200'
  const textColor = badge.textColor || config?.textColor || 'text-gray-800'
  const borderColor =
    badge.borderColor !== undefined ? badge.borderColor : config?.borderColor
  const label = badge.label || config?.label || badge.type
  const emoji = badge.emoji ?? config?.emoji ?? ''

  const borderClass = borderColor ? `border ${borderColor}` : ''

  return (
    <span
      data-badge-type={badge.type}
      className={`${bgColor} ${textColor} ${borderClass} ${SIZE_CLASSES[size]} inline-flex items-center ${className}`.trim()}
    >
      {showEmoji && emoji ? <span aria-hidden="true">{emoji}</span> : null}
      <span>{label}</span>
    </span>
  )
}
